import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getYear, getMonth } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { QuickAddOverlay } from '../components/QuickAddOverlay';
import { Budget } from '../components/BudgetItem';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { BudgetList } from '../components/BudgetList';
import { fetchBudgetsWithTransactions, checkUnreviewedEOM, ignoreEOM } from '../api/budgets';
import LoadingSpinner from '../components/LoadingSpinner';
import { EOMBanner } from '../components/EOMBanner';
import { EOMOverlay } from '../components/EOMOverlay';

const currentYear = getYear(new Date());
const currentMonth = getMonth(new Date());
const years = [currentYear, ...Array.from({ length: 10 }, (_, i) => currentYear - i - 1)];
const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function NativePillSelect({ 
  value, 
  options, 
  onChange, 
  className = "" 
}: { 
  value: string; 
  options: string[]; 
  onChange: (v: string) => void;
  className?: string;
}) {
  return (
    <div className={`relative flex-1 ${className}`}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none bg-gray-200 rounded-full px-6 py-2.5 text-base font-semibold text-gray-800 focus:outline-none select-none text-center cursor-pointer"
        style={{
          backgroundImage: 'none',
          paddingRight: '2.5rem',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          touchAction: 'manipulation'
        }}
      >
        {options.map(option => (
          <option key={option} value={option} className="text-left py-2 px-4">
            {option}
          </option>
        ))}
      </select>
      <ChevronDownIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
    </div>
  );
}

export function Home() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get year and month from URL parameters, fallback to current date
  const urlYear = searchParams.get('year');
  const urlMonth = searchParams.get('month');
  const initialYear = urlYear ? parseInt(urlYear) : currentYear;
  const initialMonth = urlMonth ? parseInt(urlMonth) : currentMonth;

  const [year, setYear] = useState(initialYear);
  const [month, setMonth] = useState(initialMonth);

  // Update URL params when year or month changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('year', year.toString());
    params.set('month', month.toString());
    setSearchParams(params, { replace: true });
  }, [year, month, searchParams, setSearchParams]);
  const [showOverlay, setShowOverlay] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState<Budget | null>(null);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showEOMBanner, setShowEOMBanner] = useState(false);
  const [eomYear, setEomYear] = useState<number | null>(null);
  const [eomMonth, setEomMonth] = useState<number | null>(null);
  const [showEOMOverlay, setShowEOMOverlay] = useState(false);

  // Fetch budgets with transactions for the selected year/month
  const loadBudgetsWithTransactions = useCallback(async (selectedYear: number, selectedMonth: number) => {
    try {
      setLoading(true);
      setShowProgressBar(false); // Reset progress bar to grey
      setError(null);
      const fetchedBudgets = await fetchBudgetsWithTransactions(selectedYear, selectedMonth);
      
      // Filter expense and expense fund budgets and transform API data to match the Budget interface
      const transformedBudgets: Budget[] = fetchedBudgets
        .filter(budget => budget.budgetType === 'Expense' || budget.budgetType === 'Expense Fund' || !budget.budgetType) // Include budgets without type for backward compatibility
        .map(budget => {
          return {
            id: budget._id,
            name: budget.name,
            icon: budget.icon,
            amount: budget.amount, // Keep original amount, let BudgetItem calculate monthly
            spent: budget.spent || 0,
            budgetType: budget.budgetType,
            savingsGoal: budget.savingsGoal,
            targetDate: budget.targetDate,
            expensePeriodValue: budget.expensePeriodValue,
            expensePeriodUnit: budget.expensePeriodUnit,
            availableAmount: (budget as any).availableAmount || 0,
            expenseAmount: (budget as any).expenseAmount || 0,
            savingsAmount: (budget as any).savingsAmount || 0
          };
        });
      
      setBudgets(transformedBudgets);
    } catch (err: any) {
      setError(err.message || 'Failed to load budgets');
    } finally {
      setLoading(false);
      // Use different animation timing based on whether it's initial load or returning from another tab
      const animationDelay = isInitialLoad ? 0 : 0;
      
      setTimeout(() => {
        setShowProgressBar(true);
      }, animationDelay);
    }
  }, [isInitialLoad]);

  // Restore scroll position when component mounts or data loads
  useEffect(() => {
    if (!loading && scrollContainerRef.current) {
      const savedScrollPosition = sessionStorage.getItem('homeScrollPosition');
      if (savedScrollPosition) {
        scrollContainerRef.current.scrollTop = parseInt(savedScrollPosition, 10);
        sessionStorage.removeItem('homeScrollPosition');
      }
    }
  }, [loading]);

  // Save scroll position when navigating away or opening overlay
  const saveScrollPosition = () => {
    if (scrollContainerRef.current) {
      sessionStorage.setItem('homeScrollPosition', scrollContainerRef.current.scrollTop.toString());
    }
  };

  // Check for unreviewed EOM
  const checkEOM = useCallback(async () => {
    try {
      const eomCheck = await checkUnreviewedEOM();
      if (eomCheck.hasUnreviewedEOM && eomCheck.year !== undefined && eomCheck.month !== undefined) {
        setShowEOMBanner(true);
        setEomYear(eomCheck.year);
        setEomMonth(eomCheck.month);
      } else {
        setShowEOMBanner(false);
      }
    } catch (err) {
      // Silently fail - don't show banner if check fails
      setShowEOMBanner(false);
    }
  }, []);

  const handleIgnoreEOM = async () => {
    if (eomYear !== null && eomMonth !== null) {
      try {
        await ignoreEOM(eomYear, eomMonth);
        setShowEOMBanner(false);
      } catch (err) {
        // Handle error silently
      }
    }
  };

  const handleViewEOM = () => {
    setShowEOMOverlay(true);
  };

  const handleEOMOverlayClose = () => {
    setShowEOMOverlay(false);
    checkEOM();
    loadBudgetsWithTransactions(year, month);
  };

  // Load data on component mount
  useEffect(() => {
    loadBudgetsWithTransactions(year, month);
    setIsInitialLoad(false);
    checkEOM();
  }, [loadBudgetsWithTransactions, year, month, checkEOM]);

  // Reload data when year or month changes
  useEffect(() => {
    loadBudgetsWithTransactions(year, month);
  }, [loadBudgetsWithTransactions, year, month]);

  // Filter budgets based on search term
  const filteredBudgets = budgets.filter(budget =>
    budget.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Separate budgets into Expense Budgets and Expense Funds, and sort alphabetically
  const expenseBudgets = filteredBudgets
    .filter(budget => budget.budgetType === 'Expense' || !budget.budgetType)
    .sort((a, b) => a.name.localeCompare(b.name));
  
  const expenseFundBudgets = filteredBudgets
    .filter(budget => budget.budgetType === 'Expense Fund')
    .sort((a, b) => a.name.localeCompare(b.name));

  // Calculate totals from expense budgets only (exclude expense fund budgets)
  const expenseBudgetsForTotals = budgets.filter(budget => budget.budgetType === 'Expense' || !budget.budgetType);
  const totalBudget = expenseBudgetsForTotals.reduce((sum, b) => sum + b.amount, 0);
  const totalSpent = expenseBudgetsForTotals.reduce((sum, b) => sum + b.spent, 0);
  const totalRemaining = totalBudget - totalSpent;
  const percentageRemaining = totalBudget > 0 ? (totalRemaining / totalBudget) * 100 : 0;
  const isOverspent = totalSpent > totalBudget;

  // Determine progress bar color
  let progressColor = 'bg-green-500';
  if (isOverspent) {
    progressColor = 'bg-red-500';
  }
  else if (percentageRemaining <= 15) {
    progressColor = 'bg-yellow-500';
  }

  return (
    <div className="min-h-screen flex flex-col items-center">
      <div className="w-full max-w-4xl mx-auto flex flex-col min-h-screen h-screen">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between pt-4 pb-2 px-4 mb-4">
          {/* <button className="p-2 rounded-lg hover:bg-gray-100">
            <Bars3Icon className="w-6 h-6 text-gray-700" />
          </button> */}
          <div className="flex-1 flex flex-col items-center">
            <img
              src="/budgie_logo_sm.png"
              alt="Budgie Logo"
              className="h-10 object-contain mx-auto"
              style={{ maxWidth: 100 }}
            />
          </div>
          {/* Empty space for symmetry */}
          {/* <div className="w-8" /> */}
        </div>
        {/* Dropdowns */}
        <div className="flex-shrink-0 flex gap-2 px-6 mb-6">
          <NativePillSelect
            value={year.toString()}
            options={years.map(y => y.toString())}
            onChange={v => setYear(Number(v))}
          />
          <NativePillSelect
            value={months[month]}
            options={months}
            onChange={v => setMonth(months.indexOf(v))}
          />
        </div>
        {/* EOM Banner */}
        {showEOMBanner && eomYear !== null && eomMonth !== null && (
          <EOMBanner
            year={eomYear}
            month={eomMonth}
            onView={handleViewEOM}
            onIgnore={handleIgnoreEOM}
          />
        )}
        {/* Overall Budget Progress */}
        <div className="flex-shrink-0 px-6 mb-6">
          <div className="flex justify-between items-end">
            <div className="text-lg text-gray-600 font-medium">Budget Remaining</div>
            {!loading && (
              <div className="text-base font-semibold text-gray-600">
                {isOverspent ? (
                  <span className="text-red-600">
                    ${Math.abs(totalRemaining).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} overspent
                  </span>
                ) : (
                  <span className={percentageRemaining <= 15 ? 'text-yellow-600' : 'text-gray-600'}>
                    ${totalRemaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} left
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="w-full h-2 bg-gray-300 rounded-full mt-2 overflow-hidden">
            <div
              className={`h-2 rounded-full transition-all ease-out ${
                showProgressBar ? progressColor : 'bg-gray-300'
              }`}
              style={{ 
                width: `${Math.min(100, Math.max(0, percentageRemaining))}%`,
                left: '0',
                transitionDuration: isInitialLoad ? '1000ms' : '300ms'
              }}
            />
          </div>
        </div>
        {/* Budget Items (scrollable) */}
        <div 
          ref={scrollContainerRef}
          className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3 px-6 pb-28 pt-6 bg-[#F8F9FA] rounded-xl"
        >
          {/* Search Input */}
          <div className="relative mb-3">
            <input
              type="text"
              placeholder="Search budgets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white rounded-full px-6 py-2.5 text-base font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all shadow-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <LoadingSpinner size="lg" />
                <p className="text-gray-500 text-sm">Loading budgets...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <p className="text-red-500 text-sm mb-2">{error}</p>
                <button 
                  onClick={() => loadBudgetsWithTransactions(year, month)}
                  className="text-green-600 text-sm font-medium hover:text-green-700"
                >
                  Try again
                </button>
              </div>
            </div>
          ) : filteredBudgets.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <p className="text-gray-500 text-sm mb-2">
                  {searchTerm ? 'No budgets match your search' : 'No budgets found'}
                </p>
                <p className="text-gray-400 text-xs">
                  {searchTerm ? 'Try adjusting your search terms' : 'Create your first budget in Settings'}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {/* Expense Budgets Section */}
              {expenseBudgets.length > 0 && (
                <div className="flex flex-col gap-3">
                  <div className="text-sm font-semibold text-gray-600 px-2">
                    Expense Budgets
                  </div>
                  <BudgetList
                    budgets={expenseBudgets}
                    onAddClick={budget => {
                      saveScrollPosition();
                      setSelectedBudget(budget);
                      setShowOverlay(true);
                    }}
                    selectedYear={year}
                    selectedMonth={month}
                    onBudgetClick={saveScrollPosition}
                  />
                </div>
              )}
              
              {/* Expense Funds Section */}
              {expenseFundBudgets.length > 0 && (
                <div className="flex flex-col gap-3">
                  <div className="text-sm font-semibold text-gray-600 px-2">
                    Expense Funds
                  </div>
                  <BudgetList
                    budgets={expenseFundBudgets}
                    onAddClick={budget => {
                      saveScrollPosition();
                      setSelectedBudget(budget);
                      setShowOverlay(true);
                    }}
                    selectedYear={year}
                    selectedMonth={month}
                    onBudgetClick={saveScrollPosition}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        {/* Overlay for Quick Add Transaction */}
        {showOverlay && selectedBudget && (
          <QuickAddOverlay
            budgets={budgets}
            initialBudget={selectedBudget}
            onClose={() => setShowOverlay(false)}
            onTransactionCreated={() => {
              // Refresh the data after transaction is created
              loadBudgetsWithTransactions(year, month);
            }}
            transactionType="expense"
            selectedYear={year}
            selectedMonth={month}
          />
        )}
        {/* EOM Overlay */}
        {showEOMOverlay && eomYear !== null && eomMonth !== null && (
          <EOMOverlay
            year={eomYear}
            month={eomMonth}
            onClose={handleEOMOverlayClose}
            onDataUpdated={() => {
              loadBudgetsWithTransactions(year, month);
            }}
          />
        )}
      </div>
    </div>
  );
} 